/**
 * O "Nova tarefa" pré-preenchido que o aceite da sugestão abre — o destino da 4.6 fora da ficha
 * (FR-21, FR-22, FR-20).
 *
 * **Ele existe só como saída do aceite**, e não é um botão de criação avulso: nem a tela de Tarefas
 * nem a fila do dia têm "Nova tarefa" no protótipo, e criar Tarefa fora de um Negócio continua
 * sendo caminho que nenhum critério pediu. O que este `Dialogo` resolve é a pendência que a 4.5
 * registrou: na ficha, aceitar a sugestão pré-preenche o formulário que já está no cartão, e "lá
 * não há formulário de criação no mesmo cartão" nas outras duas telas.
 *
 * **Navegar para a ficha do Negócio foi descartado por um motivo estrutural, e não estético.** A
 * Tarefa pode ser **solta** ou ligada só a uma Empresa (FR-20), e nesses dois casos não existe
 * ficha para onde ir — o mesmo botão levaria a lugares diferentes ou a lugar nenhum. Um `Dialogo`
 * de criação serve aos **três** vínculos, mantém a pessoa no filtro em que ela estava e mantém o
 * FR-22 literal.
 *
 * **Nenhum `POST` sai antes do submit humano** (FR-22). Abrir este diálogo não cria nada: ele
 * chega com o Tipo e o prazo da sugestão, e o Título **vazio** — que é o campo que
 * `recusaLocal` recusa em branco. É isso que faz do FR-22 uma garantia estrutural em vez de uma
 * promessa: não existe caminho em que a Tarefa nasça sem alguém ter escrito o que ela é.
 *
 * **O vínculo é copiado da Tarefa concluída, e os dois campos vão juntos.** `negocio_id` e
 * `empresa_id` são exclusivos no serviço (o `CHECK` do FR-20 torna o par irrepresentável), e copiar
 * os dois como estão preserva os três estados sem que esta tela tenha de decidir qual deles é —
 * inclusive o terceiro, a Tarefa solta, em que os dois são nulos. Escolher um deles aqui seria a
 * tela inventando um vínculo que a Tarefa de origem não tinha.
 *
 * **O Responsável é o padrão do formulário — quem entrou —, e não o da Tarefa concluída.**
 * Herdá-lo poderia atribuir a tarefa a outra pessoa sem a claim `atribuir_tarefa`, trocando um
 * convite por um `403`. É a mesma decisão que o cartão da ficha já tomou.
 *
 * **O Tipo é gravado como `id`, e o seletor resolve o excluído.** O Tipo da Tarefa de origem pode
 * ter saído de circulação (FR-69) entre a criação dela e agora: `tipoDerivado` cai no primeiro do
 * catálogo, e o prazo sugerido permanece — a mesma escada do cartão da ficha, reusada e não
 * reescrita.
 */

import { type FormEvent, useEffect, useRef, useState } from 'react'

import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { Dialogo } from './Dialogo'
import { TAMANHO_DO_TITULO, instanteDoPrazo, partesDoPrazo } from './apresentacao'
import {
  EQUIPE_INDISPONIVEL,
  type EstadoDoCatalogo,
  recusaLocal,
  rotuloDoResponsavel,
  tipoDerivado,
} from './regras-de-tarefa'
import { type SugestaoDeProximoPasso, type Tarefa, useCriarTarefa, useTiposDeTarefa } from './api'
import './ficha-do-negocio.css'
import './tarefas.css'

/** O título do diálogo — o mesmo rótulo do formulário do cartão da ficha. */
export const TITULO_DA_NOVA_TAREFA = 'Nova tarefa'

/**
 * O subtítulo, e ele diz **de onde** a sugestão veio.
 *
 * Sem ele, o diálogo apareceria com Tipo e prazo preenchidos e nada explicaria por quê — quem
 * clicou em "Agendar sugestão" saberia, mas quem chegou até aqui por teclado depois de dois
 * diálogos não necessariamente. Ele também repete o FR-21 em uma linha: nada foi criado ainda.
 */
export const SUBTITULO_DA_NOVA_TAREFA =
  'Nada foi criado ainda. Escreva o título e confirme para agendar.'

/** O rótulo do primário — ele **é** o submit, e é dele que sai o `POST`. */
export const CRIAR_TAREFA = 'Criar tarefa'

const ID_DO_TITULO = 'nova-tarefa-titulo'
const ID_DO_PRAZO = 'nova-tarefa-prazo'
const ID_DO_HORARIO = 'nova-tarefa-hora'
const ID_DO_TIPO = 'nova-tarefa-tipo'
const ID_DO_RESPONSAVEL = 'nova-tarefa-responsavel'

export interface PropriedadesDaNovaTarefaDaSugestao {
  /**
   * A Tarefa que acabou de ser concluída — a **origem** da sugestão.
   *
   * Dela saem o Tipo e o **vínculo** (os dois campos, como estão). Ela não é o corpo do `POST`: o
   * título é escrito por uma pessoa, e o Responsável é o padrão do formulário.
   */
  origem: Tarefa
  /**
   * A sugestão **do serviço** (11.6, FR-21) — a configurada para o Resultado escolhido, ou a
   * regra padrão (mesmo Tipo, 3 dias). Dela saem o Tipo e o prazo (23:59:59 do dia calculado);
   * recalculá-los aqui abriria a porta para a frase e o formulário discordarem.
   */
  sugestao: SugestaoDeProximoPasso
  /** Fecha o diálogo — por `Esc`, pelo clique no fundo, pelo "Cancelar" ou depois de criar. */
  aoFechar: () => void
}

/**
 * O diálogo de criação pré-preenchido.
 *
 * Ele é montado **só quando existe** (quem o monta o guarda atrás de um `null`), e por isso o
 * estado inicial pode nascer dos props: não há o caso de ele já estar montado quando a sugestão
 * muda. É o mesmo desenho do `key` que `<ConclusaoDeTarefa>` usa por dentro, um degrau mais
 * simples.
 */
export function NovaTarefaDaSugestao({
  origem,
  sugestao,
  aoFechar,
}: PropriedadesDaNovaTarefaDaSugestao) {
  const { data: sessao } = useSessao()
  const catalogo = useTiposDeTarefa()
  const equipe = useResponsaveis()
  const criacao = useCriarTarefa()

  const [titulo, setTitulo] = useState('')
  //: O prazo **nasce** preenchido — é a metade da sugestão que é dado, e não texto. Ele continua
  //: editável: sugerir não é obrigar (FR-21).
  const [prazo, setPrazo] = useState(() => partesDoPrazo(sugestao.prazo).data)
  const [hora, setHora] = useState(() => partesDoPrazo(sugestao.prazo).hora)
  //: E o Tipo nasce com o da Tarefa concluída, gravado como `id` cru: quem resolve o Tipo
  //: **excluído** (FR-69) é `tipoDerivado`, e escrever o `id` deixa essa decisão inteira com ela.
  const [tipo, setTipo] = useState(sugestao.tipo_de_tarefa_id)
  const [responsavel, setResponsavel] = useState('')
  const [recusa, setRecusa] = useState<string | null>(null)

  /**
   * O campo Título — o foco entra nele, porque é o único que falta preencher.
   *
   * O `Dialogo` já move o foco para dentro ao montar (ele foca o primeiro controle); este efeito o
   * leva ao campo que a pessoa tem de digitar, que é o que faz o diálogo abrir pronto para o único
   * gesto que ele espera. Sem ele, quem navega por teclado tabularia quatro campos preenchidos
   * antes de chegar ao vazio.
   */
  const campoDoTitulo = useRef<HTMLInputElement>(null)
  useEffect(() => {
    campoDoTitulo.current?.focus()
  }, [])

  const tipos = catalogo.data ?? []
  /*
   * As **duas** leituras da equipe, e a diferença entre elas é o FR-65. `daEquipe` é a lista
   * inteira, com os desativados: é ela que resolve o nome de quem já está gravado.
   * `escolhiveis` alimenta **só** as `<option>` — o que o desligado não pode é receber trabalho
   * novo.
   */
  const daEquipe = equipe.data ?? []
  const escolhiveis = daEquipe.filter((pessoa) => pessoa.ativo)

  const tipoEscolhido = tipoDerivado(tipo, tipos)
  const responsavelEscolhido = responsavel !== '' ? responsavel : (sessao?.usuario_id ?? '')
  const responsavelSemRotulo =
    responsavelEscolhido !== '' && !escolhiveis.some((pessoa) => pessoa.id === responsavelEscolhido)

  const estadoDoCatalogo: EstadoDoCatalogo = catalogo.isError
    ? 'indisponivel'
    : catalogo.isLoading
      ? 'carregando'
      : 'pronto'

  /**
   * O submit — o **único** lugar de onde sai um `POST /tarefas` nesta tela (FR-22).
   *
   * A guarda local é a mesma pura do cartão da ficha (`recusaLocal`), importada e não reescrita:
   * duas cópias divergiriam, e a que ficasse para trás deixaria passar o título em branco que o
   * serviço recusa com `400`.
   *
   * **O vínculo vai como veio.** Os dois campos são copiados da Tarefa de origem, e não escolhidos
   * aqui: o `CHECK` do FR-20 garante que nunca há os dois, e copiar preserva os três estados —
   * inclusive a Tarefa solta, em que os dois são nulos.
   */
  function enviar(evento?: FormEvent) {
    evento?.preventDefault()
    /*
     * **Com o envio no ar, o segundo `Enter` não sai.** O primário do `Dialogo` já se desabilita
     * por `ocupado`, mas o `<button type="submit">` escondido abaixo não — ele existe para que
     * `Enter` no campo Título envie, e desabilitá-lo tiraria justamente esse gesto. Sem esta
     * guarda, dois `Enter` seguidos antes da resposta criariam **duas** Tarefas iguais, que é a
     * duplicata que ninguém quer desfazer à mão.
     */
    if (criacao.isPending) {
      return
    }
    const local = recusaLocal({
      titulo,
      prazo,
      tipoEscolhido,
      responsavelEscolhido,
      catalogo: estadoDoCatalogo,
    })
    setRecusa(local)
    if (local !== null) {
      return
    }
    criacao.mutate(
      {
        titulo: titulo.trim(),
        prazo: instanteDoPrazo(prazo, hora),
        tipo_de_tarefa_id: tipoEscolhido,
        responsavel_id: responsavelEscolhido,
        negocio_id: origem.negocio_id,
        empresa_id: origem.empresa_id,
        pessoa_id: origem.pessoa_id,
      },
      {
        //: Criou: o diálogo fecha, e a lista relê sozinha (a mutação invalida `CHAVE_DAS_TAREFAS`,
        //: que é prefixo largo desde a 4.2 justamente por causa desta tela). Não há confirmação a
        //: escrever aqui: a linha nova aparece na lista atrás, que é a confirmação melhor.
        onSuccess: aoFechar,
      },
    )
  }

  function limparOsAvisos() {
    setRecusa(null)
    //: `reset()` **não** roda com o envio no ar: ele desliga o observador da mutação, e `isPending`
    //: voltaria a `false` antes de a resposta chegar — o primário reabilitaria no meio do envio e o
    //: segundo clique criaria a tarefa de novo. É a mesma guarda do formulário do cartão.
    if (!criacao.isPending) {
      criacao.reset()
    }
  }

  return (
    <Dialogo
      titulo={TITULO_DA_NOVA_TAREFA}
      subtitulo={SUBTITULO_DA_NOVA_TAREFA}
      rotuloDoConfirmar={CRIAR_TAREFA}
      ocupado={criacao.isPending}
      erro={criacao.isError ? criacao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        enviar()
      }}
    >
      {/* O `<form>` existe para que `Enter` no campo Título envie, e o `onSubmit` chama a
          **mesma** função do primário do rodapé — as duas portas nunca divergem.

          **E o `Enter` só funciona por causa do botão escondido logo abaixo.** A submissão
          implícita do HTML precisa de um botão de submit **dentro** do formulário, e o confirmar
          deste diálogo mora no rodapé do `Dialogo`, fora dele. Sem esse botão, `Enter` no campo de
          texto não faz nada — e a promessa de "envia como em qualquer formulário" seria falsa
          justamente para quem digita o título e aperta a tecla sem tirar a mão do teclado. */}
      <form className="nova-tarefa" onSubmit={enviar}>
        {/* `aria-hidden` e `tabIndex={-1}`: ele não é um segundo "Criar tarefa" na tela nem na
            ordem de tabulação — é só o alvo que a submissão implícita exige. O rodapé do `Dialogo`
            continua sendo o único confirmar que alguém vê. */}
        <button type="submit" className="nova-tarefa__submit" aria-hidden="true" tabIndex={-1} />
        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID_DO_TITULO}>
            Título
          </label>
          {/* Vazio de propósito, e é ele que faz do FR-22 uma garantia estrutural: o protótipo não
              sugere título nenhum, e `recusaLocal` recusa o branco. */}
          <input
            id={ID_DO_TITULO}
            ref={campoDoTitulo}
            className="ficha__entrada"
            type="text"
            value={titulo}
            maxLength={TAMANHO_DO_TITULO}
            onChange={(evento) => {
              limparOsAvisos()
              setTitulo(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID_DO_PRAZO}>
            Prazo
          </label>
          {/* **Sem `min`**, e a ausência é deliberada: prazo no passado é legítimo, e a tarefa
              nasce vencida — que é o caso que o vermelho existe para denunciar. */}
          <input
            id={ID_DO_PRAZO}
            className="ficha__entrada"
            type="date"
            value={prazo}
            onChange={(evento) => {
              limparOsAvisos()
              setPrazo(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID_DO_HORARIO}>
            Hora (opcional)
          </label>
          {/* Vazia, o prazo vale até o fim do dia (23:59:59 em São Paulo). */}
          <input
            id={ID_DO_HORARIO}
            className="ficha__entrada"
            type="time"
            value={hora}
            onChange={(evento) => {
              limparOsAvisos()
              setHora(evento.target.value)
            }}
          />
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID_DO_TIPO}>
            Tipo
          </label>
          <select
            id={ID_DO_TIPO}
            className="ficha__selecao"
            value={tipoEscolhido}
            onChange={(evento) => {
              limparOsAvisos()
              setTipo(evento.target.value)
            }}
          >
            {/* O catálogo excluído não aparece: `GET /tipos-de-tarefa` já filtra `ativo`, e é este
                o caminho das tarefas **novas** (FR-69). */}
            {tipos.map((umTipo) => (
              <option key={umTipo.id} value={umTipo.id}>
                {umTipo.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="nova-tarefa__campo">
          <label className="ficha__linha-rotulo" htmlFor={ID_DO_RESPONSAVEL}>
            Responsável
          </label>
          <select
            id={ID_DO_RESPONSAVEL}
            className="ficha__selecao"
            value={responsavelEscolhido}
            onChange={(evento) => {
              limparOsAvisos()
              setResponsavel(evento.target.value)
            }}
          >
            {/* A `<option>` sintética preserva a escolha que a lista não conhece — a equipe não
                chegou, falhou, ou o escolhido saiu dela por desativação. Sem ela o controle cai
                calado na primeira pessoa e o envio sai no nome de outra. */}
            {responsavelSemRotulo ? (
              <option value={responsavelEscolhido}>
                {rotuloDoResponsavel(responsavelEscolhido, { sessao, daEquipe })}
              </option>
            ) : null}
            {escolhiveis.map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {pessoa.nome}
              </option>
            ))}
          </select>
        </div>

        {/* A recusa local vem **antes** da do serviço, que o `Dialogo` desenha logo abaixo: quando
            ela existe, a ida à rede nem aconteceu. */}
        {recusa !== null ? (
          <p className="ficha__erro" role="alert">
            {recusa}
          </p>
        ) : null}

        {/* E a falha ao ler a equipe é dita aqui também, e não só no formulário do cartão da
            ficha: o envio continua saindo — o Responsável cai em quem entrou, e a `<option>`
            sintética acima o mostra pelo nome —, e por isso o aviso não manda parar. Ele diz em
            nome de quem a Tarefa vai nascer se ninguém recarregar. Sem ele, esta superfície teria
            um seletor de uma pessoa só, sem explicação nenhuma. */}
        {equipe.isError ? (
          <p className="ficha__erro" role="alert">
            {EQUIPE_INDISPONIVEL}
          </p>
        ) : null}
      </form>
    </Dialogo>
  )
}
